// Series primitive that paints solid colored bands across a Trend Gauge
// sub-pane (Pressure / Buy Risk / TPR) — one filled rectangle per
// contiguous same-state run, drawn directly on canvas rather than as
// individual chart series.
//
// Modeled on PowerTrendBackground (see that file for the underlying
// technique): logicalToCoordinate only accepts integer bar indices, so
// each band is looked up by its start/end bar index and extended by half a
// barSpacing on each side — which is also what makes adjacent bands paint
// as one continuous strip with no seam, no point-sharing tricks needed.
// The binary-search index lookup is shared with that file rather than
// duplicated — see findIndexAtOrAfter/findIndexAtOrBefore there.
//
// Attached via `series.attachPrimitive(...)` to a lightweight (invisible)
// anchor series that exists only to host this primitive and give its pane
// a stable identity — see ensureTrendGaugePanes in lightweight_chart_controller.js.

import { findIndexAtOrAfter, findIndexAtOrBefore } from './power_trend_background'

const DEFAULT_BAR_SPACING = 6

class TrendGaugeBandRenderer {
  constructor(data) {
    this._data = data
  }

  draw(target) {
    const { chart, bands, data } = this._data
    if (!chart || !data || data.length === 0 || !bands?.length) return

    target.useBitmapCoordinateSpace(scope => {
      const ctx = scope.context
      const timeScale = chart.timeScale()
      const h = scope.bitmapSize.height
      const pr = scope.horizontalPixelRatio

      const barSpacing = timeScale.options().barSpacing || DEFAULT_BAR_SPACING
      const halfBar = barSpacing / 2

      const xOf = (idx, offset) => {
        const centre = timeScale.logicalToCoordinate(idx)
        return centre === null ? null : (centre + offset) * pr
      }

      bands.forEach(({ color, fromDate, toDate }) => {
        const fromIdx = findIndexAtOrAfter(data, fromDate)
        const toIdx = findIndexAtOrBefore(data, toDate)
        if (fromIdx > toIdx || fromIdx >= data.length || toIdx < 0) return

        const x1 = xOf(fromIdx, -halfBar)
        const x2 = xOf(toIdx, halfBar)
        if (x1 === null || x2 === null) return

        ctx.fillStyle = color
        ctx.fillRect(x1, 0, x2 - x1, h)
      })
    })
  }
}

class TrendGaugeBandPaneView {
  constructor(data, zOrder) {
    this._data = data
    this._zOrder = zOrder
  }

  update(data) {
    this._data = data
  }

  renderer() {
    return new TrendGaugeBandRenderer(this._data)
  }

  zOrder() {
    return this._zOrder
  }
}

// A pane paints in the order: 'bottom' primitives, GRID, 'normal' primitives.
// The default is 'bottom' because the /leaders stage tint (price_chart_renderer)
// must sit BEHIND the candles. The gauge strips pass 'normal' instead: the grid
// is a chart-wide option with no per-pane override, so painting the bands over
// it is the only way to keep gridlines on the price pane while the thin strips
// below stay solid colour.
const DEFAULT_Z_ORDER = 'bottom'

export class TrendGaugeBand {
  constructor({ bands, data, zOrder = DEFAULT_Z_ORDER } = {}) {
    this._sharedData = { chart: null, bands: bands || [], data: data || [] }
    this._paneViews = [ new TrendGaugeBandPaneView(this._sharedData, zOrder) ]
  }

  attached(param) {
    this._sharedData.chart = param.chart
    this.updateAllViews()
    param.requestUpdate()
  }

  detached() {
    this._sharedData.chart = null
  }

  // Replace the bands/data in place and repaint — used whenever the
  // selected ticker changes, so the primitive doesn't need to be torn
  // down and reattached each time.
  update({ bands, data }) {
    this._sharedData.bands = bands || []
    this._sharedData.data = data || []
    this.updateAllViews()
  }

  updateAllViews() {
    this._paneViews.forEach(pv => pv.update(this._sharedData))
  }

  paneViews() {
    return this._paneViews
  }
}
